/* eslint-disable react/jsx-key */
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { createKey, readKey } from '@/api/params';
import useSWR from 'swr';
import { SortHeader } from '@/components/List/Header/SortHeader';
import { postFetcher, getFetcher } from '@/api/fetchers';
import { List } from '@/components/List/List';
import { formatDate } from '@/utils/Date';
import { useDisclosure } from '@mantine/hooks';
import { Button, Input, Select, Text } from '@mantine/core';
import { toNumber } from 'lodash';
import { TextHeader } from '@/components/List/Header/TextHeader';
import { ClampedTextCell } from '@/components/List/Cell/ClampedTextCell';
import { AlignedCell } from '@/components/List/Cell/AlignedCell';
import { Badge } from '@/components/Badge/Badge';
import { MappingToComboBoxItem } from '@/schemas/converters/mappingShemasConverter';
import type { ApplicantsRequest } from '@/schemas/API/GET/applicant/request';
import type {
  Applicant,
  Applicants,
} from '@/schemas/API/GET/applicant/response';
import { type Mapping } from '@/schemas/API/GET/mapping/response';
import { NewFormModal } from './Components/NewFormModal';
import { useExportStatus } from '@/hooks/export/useExportStatus';
import { downloadFileWithBearerToken } from '@/utils/functions';

const ApplicantNameCell = (applicant: Applicant) => (
  <ClampedTextCell>
    {applicant.lastName} {applicant.firstName}
  </ClampedTextCell>
);

const ApplicantUpdatedAtCell = (applicant: Applicant) => (
  <Text>{formatDate(applicant.updatedAt)}</Text>
);

const ApplicantAddressCell = (applicant: Applicant) => (
  <ClampedTextCell>
    {applicant.servAddress.street}, {applicant.servAddress.city}
  </ClampedTextCell>
);

const ApplicantFormStatusCell = (getBadge: (state: string) => JSX.Element) => {
  const Cell = (applicant: Applicant) => (
    <AlignedCell align="center">{getBadge(applicant.currState)}</AlignedCell>
  );
  return Cell;
}

export const ApplicantsListPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [
    openedNewFormModal,
    { open: openNewFormModal, close: closeNewFormModal },
  ] = useDisclosure(false);
  const [requestParams, setRequestParams] = useState<ApplicantsRequest>({
    page: 1,
    sort: [],
    size: 10,
  });

  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const queryParams = readKey(window.location.search);
    setRequestParams((currentParams: any) => ({
      ...currentParams,
      ...queryParams,
    }));
  }, []);

  useEffect(() => {
    navigate(createKey(requestParams), { replace: true });
  }, [requestParams, navigate]);

  const { data: stateMap } = useSWR<Mapping>(
    '/mappings/applicant-states',
    getFetcher
  );
  const { data: applicants } = useSWR<Applicants>(
    '/applicants' + createKey(requestParams),
    getFetcher
  );

  const setRequestParam = <K extends keyof ApplicantsRequest>(
    fieldName: K,
    value: ApplicantsRequest[K] | null
  ): void => {
    setRequestParams(prevParams => {
      const newParams = { ...prevParams, [fieldName]: value };
      const filterFields: Array<keyof ApplicantsRequest> = ['query', 'state'];
      if (filterFields.includes(fieldName)) {
        newParams.page = 1;
      }

      return newParams;
    });
  };

  const { checkExportStatus } = useExportStatus();
  const handleApplicantsExport = async () => {
    try {
      setIsExporting(true);
      const currentRequestParams = { ...requestParams };
      const requestData = {
        filter: {
          query: currentRequestParams.query,
          state: currentRequestParams.state,
          serv: currentRequestParams.serv,
        },
      };

      const { exportId } = await postFetcher(
        '/exports/applicants',
        requestData
      );
      const exportStatus = await checkExportStatus(exportId);
      if (exportStatus?.state === 'FINISHED' && exportStatus?.url) {
        await downloadFileWithBearerToken(exportStatus.url);
      } else {
        console.error('Export failed or no download URL available.');
      }
    } catch (error) {
      console.error('Error exporting applicants:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const SearchBar = (
    <Input.Wrapper label="Vyhledat">
      <Input
        placeholder="např: Václav"
        value={requestParams?.query}
        onChange={e => setRequestParam('query', e.currentTarget.value)}
      />
    </Input.Wrapper>
  );

  const StateSelector = (
    <Select
      placeholder="Všechny"
      label="Stav formuláře"
      value={String(requestParams.state)}
      onChange={e => setRequestParam('state', e)}
      data={stateMap ? MappingToComboBoxItem(stateMap) : []}
      clearable
    />
  );

  const getStateBadge = (state: string) => {
    let color = 'error';
    switch (state) {
      case 'ACTIVE':
        color = 'success';
        break;
      case 'QUEUED':
      case 'WAITING':
        color = 'warning';
        break;
      default:
        break;
    }

    return <Badge label={stateMap ? stateMap[state] : ''} color={color} />;
  };

  return (
    <div className="h-full p-8 overflow-auto">
      <List
        name="Zájemci"
        pagination={{
          page: Number(requestParams.page),
          setPage: e => setRequestParam('page', e),
          pageTotal: applicants?.totalPages ?? 1,
        }}
        selectors={[SearchBar, StateSelector]}
        loading={!applicants}
        controls={[
          <Button
            variant="light"
            key="export-excel-button"
            onClick={handleApplicantsExport}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>,
          <Button
            key="add-applicant-button"
            onClick={() => {
              openNewFormModal();
            }}
          >
            Nový formulář
          </Button>,
        ]}
        perPageSelector={{
          label: 'Zájemců na stránce',
          value: toNumber(requestParams.size),
          setter: n => setRequestParam('size', n),
        }}
        columns={[
          <SortHeader
            label="Příjmení a jméno"
            fields={['lastName', 'firstName']}
            callback={sort => setRequestParam('sort', sort)}
            currentSort={requestParams.sort ?? []}
          />,
          <TextHeader>Datum Poslední úpravy</TextHeader>,
          <TextHeader>Bydliště</TextHeader>,
          <TextHeader align="center">Stav formuláře</TextHeader>,
        ]}
        content={{
          items: applicants?.content ?? [],
          onClick: (applicant: Applicant) =>
            navigate(`/applicants/${applicant.id}`),
          renders: [
            ApplicantNameCell,
            ApplicantUpdatedAtCell,
            ApplicantAddressCell,
            ApplicantFormStatusCell(getStateBadge),
          ],
        }}
      />
      <NewFormModal opened={openedNewFormModal} close={closeNewFormModal} />
    </div>
  );
};
